import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import AdminShell from '../../components/AdminShell.jsx';
import Alert from '../../components/Alert.jsx';
import Loader from '../../components/Loader.jsx';
import { api } from '../../api/client.js';

// /admin/audit-log — read-only feed of the audit_log table. Every admin-side
// mutation (_lib/audit.js callers) lands here. Filter by category; page
// forward/back through history; click a row's Details to see the full payload
// with overlay ids resolved to names + links.
//
// Pagination is keyset (the endpoint returns a `nextCursor` = createdAt of the
// last row). Forward paging follows that cursor; to page *back* we keep the
// per-page `before` cursor in a stack so Previous re-fetches the page we came
// from. No COUNT / OFFSET — stays cheap as the table grows.
const FILTERS = [
  { key: '', label: 'All' },
  { key: 'user', label: 'Users' },
  { key: 'accessRequest', label: 'Access' },
  { key: 'designRequest', label: 'Design' },
];

// Actions are namespaced ("user.update", "grant.applyDefault") — tint the mono
// action cell by namespace. grant.* reads as access provisioning.
function actionTint(action = '') {
  if (action.startsWith('user')) return ' adm-audit-act--user';
  if (action.startsWith('designRequest')) return ' adm-audit-act--design';
  if (action.startsWith('accessRequest') || action.startsWith('grant')) return ' adm-audit-act--access';
  return '';
}

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isOverlayKey = (k) => /overlay/i.test(k);

// "autoApprovedRequestIds" → "auto Approved Request Ids" (CSS uppercases it).
// Splitting camelCase + underscores gives spaced, wrappable labels instead of
// one long unbreakable token.
const humanizeKey = (k) =>
  k.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();

// Compact one-value summary for the table cell. The full value lives in the
// modal, so this only needs to be scannable, not complete.
function summarize(v) {
  if (v === null || v === undefined || v === '') return { text: '—', muted: true };
  if (Array.isArray(v)) {
    if (v.length === 0) return { text: 'none', muted: true };
    return { text: `${v.length} item${v.length === 1 ? '' : 's'}` };
  }
  if (typeof v === 'object') {
    const flat = JSON.stringify(v);
    return { text: flat.length > 40 ? `${flat.slice(0, 40)}…` : flat };
  }
  const s = String(v);
  if (s.length > 30) return { text: `${s.slice(0, 12)}…${s.slice(-6)}` };
  return { text: s };
}

// One line, not a stacked key/value block. The old layout made a
// grant.useDefault row ~90px tall against a user.update row's ~30px, so the
// scan columns (when/actor/action/target) got squeezed into the left third and
// row-height variance made the table hard to read — the one thing an audit log
// has to be good at (brief §5.11). The full payload is unchanged in the modal.
const SUMMARY_KEYS = 3;

function PayloadSummary({ payload }) {
  const entries = Object.entries(payload);
  const shown = entries.slice(0, SUMMARY_KEYS);
  const rest = entries.length - shown.length;

  return (
    <span className="audit-detail adm-audit-line">
      {shown.map(([k, v], i) => {
        const { text, muted } = summarize(v);
        return (
          <span className="audit-detail__pair" key={k}>
            <span className="audit-detail__key">{humanizeKey(k)}</span>
            <span className={`audit-detail__val${muted ? ' audit-detail__val--muted' : ''}`}>
              {text}
            </span>
            {(i < shown.length - 1 || rest > 0) && (
              <span className="adm-audit-line__sep" aria-hidden="true">·</span>
            )}
          </span>
        );
      })}
      {rest > 0 && <span className="audit-detail__val--muted">+{rest} more</span>}
    </span>
  );
}

function OverlayLink({ id, overlaysById, onNavigate }) {
  const o = overlaysById?.[id];
  if (!o) {
    return (
      <span>
        <code>{id}</code> <span className="text-muted small">(deleted)</span>
      </span>
    );
  }
  const body = (
    <>
      {o.name}
      {o.packageName && <span className="text-muted small"> · {o.packageName}</span>}
    </>
  );
  // Link only works when we know which package page to land on.
  return o.packageSlug ? (
    <Link to={`/${o.packageSlug}`} className="audit-modal__overlay-link" onClick={onNavigate}>
      {body}
    </Link>
  ) : (
    <span>{body}</span>
  );
}

function FieldValue({ fieldKey, value, overlaysById, onNavigate }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted">—</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted">none</span>;
    const overlayish = isOverlayKey(fieldKey);
    return (
      <ul className="audit-modal__list">
        {value.map((item, i) => (
          <li key={i}>
            {overlayish && typeof item === 'string' && UUID_RE.test(item) ? (
              <OverlayLink id={item} overlaysById={overlaysById} onNavigate={onNavigate} />
            ) : (
              <code>{typeof item === 'object' ? JSON.stringify(item) : String(item)}</code>
            )}
          </li>
        ))}
      </ul>
    );
  }
  if (isOverlayKey(fieldKey) && typeof value === 'string' && UUID_RE.test(value)) {
    return <OverlayLink id={value} overlaysById={overlaysById} onNavigate={onNavigate} />;
  }
  if (typeof value === 'object') {
    return <pre className="audit-modal__json">{JSON.stringify(value, null, 2)}</pre>;
  }
  return <code>{String(value)}</code>;
}

function DetailModal({ entry, overlaysById, onClose }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  const payload = entry.payload && typeof entry.payload === 'object' ? entry.payload : {};
  const fields = Object.entries(payload);

  return createPortal(
    <>
      <div className="confirm-modal-backdrop" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-detail-title"
        className="confirm-modal-layer"
        onClick={onClose}
      >
        <div
          className="confirm-modal-panel"
          onClick={(e) => e.stopPropagation()}
          style={{ '--cm-accent': 'var(--neon-blue)', '--cm-glow': 'var(--neon-blue-dim)' }}
        >
          <div className="confirm-modal-inner">
            <div className="confirm-modal-header">
              <h5 id="audit-detail-title" className="confirm-modal-title">Audit entry</h5>
              <button type="button" className="confirm-modal-close" aria-label="Close" onClick={onClose}>
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="confirm-modal-body">
              <dl className="audit-modal__meta">
                <div>
                  <dt>When</dt>
                  <dd>{new Date(entry.createdAt).toLocaleString()}</dd>
                </div>
                <div>
                  <dt>Actor</dt>
                  <dd>{entry.actor?.username ? <code>{entry.actor.username}</code> : <span className="text-muted">system</span>}</dd>
                </div>
                <div>
                  <dt>Action</dt>
                  <dd><code>{entry.action}</code></dd>
                </div>
                <div>
                  <dt>Target</dt>
                  <dd>{entry.target?.username ? <code>{entry.target.username}</code> : <span className="text-muted">—</span>}</dd>
                </div>
              </dl>

              <hr className="audit-modal__divider" />

              {fields.length === 0 ? (
                <p className="text-muted mb-0">No additional details.</p>
              ) : (
                <dl className="audit-modal__fields">
                  {fields.map(([k, v]) => (
                    <div className="audit-modal__field" key={k}>
                      <dt>{humanizeKey(k)}</dt>
                      <dd>
                        <FieldValue
                          fieldKey={k}
                          value={v}
                          overlaysById={overlaysById}
                          onNavigate={onClose}
                        />
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>

            <div className="confirm-modal-footer">
              <button type="button" className="btn-ghost" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

function hasPayload(p) {
  return p && typeof p === 'object' && Object.keys(p).length > 0;
}

export default function AuditLog() {
  const [category, setCategory] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [entries, setEntries] = useState(null);
  const [overlaysById, setOverlaysById] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  // `before` cursor used to fetch each page; cursors[0] is null (newest page).
  const [cursors, setCursors] = useState([null]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  const fetchPage = useCallback(async (cat, before, index, limit) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.adminListAuditLog({
        category: cat || undefined,
        before: before || undefined,
        limit,
      });
      setEntries(data?.entries ?? []);
      setOverlaysById(data?.overlays ?? {});
      setNextCursor(data?.nextCursor ?? null);
      setPageIndex(index);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset to the first (newest) page whenever the category filter or page
  // size changes — the cursor stack is only valid for a fixed page size.
  useEffect(() => {
    setEntries(null);
    setCursors([null]);
    fetchPage(category, null, 0, pageSize);
  }, [category, pageSize, fetchPage]);

  function goNext() {
    if (!nextCursor || loading) return;
    const before = nextCursor;
    setCursors((cs) => {
      const copy = cs.slice(0, pageIndex + 1);
      copy[pageIndex + 1] = before;
      return copy;
    });
    fetchPage(category, before, pageIndex + 1, pageSize);
  }

  function goPrev() {
    if (pageIndex === 0 || loading) return;
    fetchPage(category, cursors[pageIndex - 1], pageIndex - 1, pageSize);
  }

  const hasPrev = pageIndex > 0;
  const hasNext = !!nextCursor;

  return (
    <AdminShell crumb="Audit Log" stripStatus="Append-only">
      <header className="adm-head adm-rv" style={{ animationDelay: '80ms' }}>
        <h1 className="adm-head__title">Audit Log</h1>
        <p className="adm-head__sub">
          Append-only record of every admin-side mutation. Click a row&apos;s details for the
          full payload with overlay ids resolved.
        </p>
      </header>

      <div className="adm-filterbar adm-rv" style={{ animationDelay: '140ms' }}>
        <div className="adm-pills" role="group" aria-label="Filter by category">
          {FILTERS.map((f) => (
            <button
              key={f.key || 'all'}
              type="button"
              className={`adm-pill${f.key === category ? ' is-active' : ''}`}
              onClick={() => setCategory(f.key)}
              aria-pressed={f.key === category}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <Alert kind="danger" onClose={() => setError(null)}>{error}</Alert>
      )}

      {entries === null ? (
        <Loader label="Loading…" />
      ) : (
        <div
          className={`adm-scroll adm-scroll--cards adm-scroll--audit adm-rv${loading ? ' opacity-50' : ''}`}
          style={{
            '--adm-cols': '200px 160px 250px 160px 1fr',
            '--adm-minw': '1000px',
            animationDelay: '220ms',
          }}
        >
          <div className="adm-grid__head">
            <span>When</span>
            <span>Actor</span>
            <span>Action</span>
            <span>Target</span>
            <span>Details</span>
          </div>
          <div className="adm-grid__rule" aria-hidden="true" />
          {entries.map((e, i) => (
            <div
              className="adm-grid__row adm-rvl"
              key={e.id}
              style={{ minHeight: 56, animationDelay: `${70 + i * 35}ms` }}
            >
              {/* display:contents on desktop (children keep their columns);
                  on phones the wrapper is the card's single chip line:
                  action first, BY actor, TO target, the timestamp closing. */}
              <div className="adm-grid__meta">
                <div className="adm-cell adm-cell--dim">
                  {new Date(e.createdAt).toLocaleString()}
                </div>
                <div className="adm-cell" data-mlabel="by">
                  {e.actor?.username
                    ? <span className="adm-cell--main" style={{ fontSize: 15 }}>{e.actor.username}</span>
                    : <span className="adm-cell--faint">system</span>}
                </div>
                <div>
                  <span className={`adm-audit-act${actionTint(e.action)}`}>{e.action}</span>
                </div>
                <div className="adm-cell" data-mlabel={e.target?.username ? 'to' : undefined}>
                  {e.target?.username ?? <span className="adm-cell--faint">—</span>}
                </div>
              </div>
              <div>
                {hasPayload(e.payload) ? (
                  <button
                    type="button"
                    className="audit-detail-trigger"
                    onClick={() => setDetail(e)}
                    title="View full details"
                  >
                    <PayloadSummary payload={e.payload} />
                  </button>
                ) : (
                  <span className="adm-cell--faint">—</span>
                )}
              </div>
            </div>
          ))}
          {entries.length === 0 && (
            <div className="adm-empty">
              {pageIndex === 0 ? 'No activity recorded yet.' : 'No activity on this page.'}
            </div>
          )}

          <div className="adm-pager">
            <span className="adm-pager__label">Rows per page</span>
            <div className="adm-pager__sizes" role="group" aria-label="Rows per page">
              {PAGE_SIZE_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`adm-pager__size${pageSize === n ? ' is-active' : ''}`}
                  onClick={() => setPageSize(n)}
                  disabled={loading}
                  aria-pressed={pageSize === n}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="adm-pager__nav">
              <button
                type="button"
                className="adm-pager__btn"
                onClick={goPrev}
                disabled={!hasPrev || loading}
              >
                <ChevronLeft size={14} aria-hidden="true" /> Prev
              </button>
              <span className="adm-pager__page">Page {pageIndex + 1}</span>
              <button
                type="button"
                className="adm-pager__btn"
                onClick={goNext}
                disabled={!hasNext || loading}
              >
                Next <ChevronRight size={14} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <DetailModal
          entry={detail}
          overlaysById={overlaysById}
          onClose={() => setDetail(null)}
        />
      )}
    </AdminShell>
  );
}
